import type {
  CampaignColumn,
  CampaignColumnValue,
} from 'services/campaignColumns'
import {
  isUsableCampaignColumnValue,
  useApprovedCampaignColumnValues,
} from './useApprovedCampaignColumnValues'

type AITokenMap = Record<string, string>

export function buildCampaignAITokensByContact(
  columns: ReadonlyArray<CampaignColumn>,
  valueRowsByColumn: ReadonlyArray<
    ReadonlyArray<CampaignColumnValue> | undefined
  >
): Record<number, AITokenMap> {
  const byContact: Record<number, AITokenMap> = {}
  valueRowsByColumn.forEach((rows, index) => {
    const column = columns[index]
    if (!column) return
    const slug = column.merge_variable_slug.toUpperCase()
    for (const row of rows ?? []) {
      const text = row.value?.value?.trim() ?? ''
      if (!isUsableCampaignColumnValue(row)) continue
      if (!byContact[row.contact]) byContact[row.contact] = {}
      byContact[row.contact][slug] = text
    }
  })
  return byContact
}

/**
 * Build a per-contact token map of computed AI-column values for the campaign.
 *
 * Returned shape: `{ [contactId]: { [SLUG_UPPER]: stringValue } }`.
 *
 * Used by the sequence builder's preview pane so AI variables (e.g.
 * `{SHORT_NAME}`) interpolate alongside the standard `{FIRST_NAME}` set.
 */
export function useCampaignAITokensByContact(
  campaignId: number | undefined
): Record<number, AITokenMap> {
  const { columns, valueQueries } = useApprovedCampaignColumnValues(campaignId)

  return buildCampaignAITokensByContact(
    columns,
    valueQueries.map((query) => query.data?.results)
  )
}
